import React, { useContext } from "react"
import { useHistory } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import moment from "moment"
import { BarChart2, Layers, Package, ShoppingBag, Truck } from "react-feather"

import MobileHeader from "../components/MobileHeader"
import { GET_MOBILE_LIST } from "../queries"
import { GET_HEADER_STATISTICS } from "../../rest-api/queries/headerStatistics"
import { AuthContext } from "../../contexts/auth/AuthContext"
import useMobileSections from "../useMobileSections"
import { num } from "../format"

/** Faqat sonini bilish uchun so'rov — bitta yozuv so'raladi */
function useCount(key: string, url: string, extra?: Record<string, any>, enabled = true) {
  return useQuery(
    ["MOBILE_COUNT", key],
    () => GET_MOBILE_LIST({ url, pageNumber: 1, pageSize: 1, extra }),
    { enabled, staleTime: 60 * 1000, retry: false }
  )
}

/**
 * Mobil bosh sahifa.
 *
 * Yuqorida bugungi tushum, ostida to'rtta ko'rsatkich, pastida oxirgi
 * savdolar. Ko'rsatkichlar uchun ro'yxat so'rovlaridan faqat yozuvlar
 * SONI olinadi (bitta yozuv so'raladi) — telefonga ortiqcha ma'lumot
 * yuklanmasin.
 */
export default function MobileHome() {
  const { t } = useTranslation()
  const history = useHistory()
  const { authState } = useContext(AuthContext)
  const { sections } = useMobileSections()

  const has = (code: string) => sections.some((s) => s.code === code)

  const { data: stats, isLoading: statsLoading } = useQuery(
    ["GET_HEADER_STATISTICS"],
    GET_HEADER_STATISTICS,
    { staleTime: 60 * 1000 }
  )

  const stat: any = (stats as any)?.headerProductCounterpartyCount || stats || {}

  const today = moment().format("YYYY-MM-DD")
  const salesToday = useCount("salesToday", "Sales", { begin: today, end: today }, has("sales"))
  const orders = useCount("orders", "Orders", undefined, has("orders"))
  const deliveries = useCount("deliveries", "Deliveries", undefined, true)
  const warehouses = useCount("warehouses", "Warehouses", undefined, true)

  const recent = useQuery(
    ["MOBILE_RECENT_SALES"],
    () => GET_MOBILE_LIST({ url: "Sales", pageNumber: 1, pageSize: 4 }),
    { enabled: has("sales"), staleTime: 30 * 1000 }
  )

  const tiles = [
    {
      show: has("sales"),
      labelKey: "sale",
      noteKey: "mobToday",
      value: salesToday.data?.totalRecords ?? 0,
      icon: ShoppingBag,
      color: "#16A34A",
      tint: "#DCFCE7",
      path: "/m/sales",
    },
    {
      show: has("orders"),
      labelKey: "orders",
      noteKey: "mobActive",
      value: orders.data?.totalRecords ?? 0,
      icon: Package,
      color: "#EA580C",
      tint: "#FFEDD5",
      path: "/m/orders",
    },
    {
      show: true,
      labelKey: "logistics",
      noteKey: "mobOnWay",
      value: deliveries.data?.totalRecords ?? 0,
      icon: Truck,
      color: "#2563EB",
      tint: "#DBEAFE",
      path: "/m/more",
    },
    {
      show: true,
      labelKey: "warehouses",
      noteKey: "mobActive",
      value: warehouses.data?.totalRecords ?? 0,
      icon: Layers,
      color: "#7C3AED",
      tint: "#EDE9FE",
      path: "/m/more",
    },
  ].filter((x) => x.show)

  return (
    <div className="mb-page">
      <MobileHeader title={t("mobHome")} onMenu={() => history.push("/m/more")} />

      <div className="mb-body">
        {/* Salomlashuv */}
        <div className="mb-greeting">
          <div>
            <div className="mb-greeting-hi">{t("mobWelcome")},</div>
            <div className="mb-greeting-name">{authState.user?.userName || "—"}</div>
            <div className="mb-greeting-status">
              <i /> {t("mobOnline")}
            </div>
          </div>
          <div className="mb-avatar">
            <BarChart2 size={22} />
          </div>
        </div>

        {/* Bugungi tushum */}
        <div className="mb-revenue">
          <div className="mb-revenue-main">
            <div className="mb-revenue-label">{t("mobTodayProfit")}</div>
            <div className="mb-revenue-value">
              {statsLoading ? "…" : `$${num(stat?.todayProfitUSD || 0)}`}
            </div>
            <div className="mb-revenue-note">{t("mobTodayNote")}</div>
          </div>
          <BarChart2 size={40} className="mb-revenue-icon" />
        </div>

        {/* Ko'rsatkichlar */}
        <div className="mb-tiles">
          {tiles.map((tile) => (
            <button
              key={tile.labelKey}
              type="button"
              className="mb-tile"
              onClick={() => history.push(tile.path)}
            >
              <div className="mb-tile-head">
                <span className="mb-tile-label">{t(tile.labelKey)}</span>
                <span className="mb-tile-icon" style={{ background: tile.tint, color: tile.color }}>
                  <tile.icon size={16} />
                </span>
              </div>
              <div className="mb-tile-value">{num(tile.value)}</div>
              <div className="mb-tile-note">{t(tile.noteKey)}</div>
            </button>
          ))}
        </div>

        {/* Oxirgi savdolar */}
        {has("sales") && (
          <div className="mb-recent">
            <div className="mb-recent-head">
              <h4>{t("mobRecentSales")}</h4>
              <button type="button" onClick={() => history.push("/m/sales")}>
                {t("dashSeeAll")}
              </button>
            </div>

            {recent.isLoading ? (
              <div className="mb-skeleton">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="mb-skeleton-row" />
                ))}
              </div>
            ) : (recent.data?.data || []).length === 0 ? (
              <div className="mb-empty">{t("noData")}</div>
            ) : (
              (recent.data?.data || []).map((row: any) => (
                <div className="mb-recent-row" key={row.id}>
                  <span className="mb-recent-icon">
                    <ShoppingBag size={15} />
                  </span>
                  <div className="mb-recent-main">
                    <div className="mb-recent-name">
                      {row?.mainCounterparty?.name || "—"}
                    </div>
                    <div className="mb-recent-note">
                      {row?.mainWarehouse?.name || ""}
                      {row?.dateSale
                        ? ` · ${moment(row.dateSale).format("DD.MM.YYYY HH:mm")}`
                        : ""}
                    </div>
                  </div>
                  <div className="mb-recent-sum">${num(row?.totalPriceUSD)}</div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}
